import React, { useMemo } from 'react';
import type { SimWorld } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';

interface StatsGraphPanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

/**
 * 統計グラフ表示パネル
 */
export const StatsGraphPanel: React.FC<StatsGraphPanelProps> = ({ world, onNPCClick, isMobile = false }) => {
  // 統計データの計算
  const stats = useMemo(() => {
    const alive = world.npcs.filter((npc) => npc.isAlive);
    const dead = world.npcs.filter((npc) => !npc.isAlive);

    // デュエル統計
    const totalDuels = world.npcs.reduce(
      (sum, npc) => sum + npc.wins + npc.losses,
      0
    ) / 2; // 重複除去

    // レーティングランキング（トップ5）
    const ratingRanking = [...world.npcs]
      .sort((a, b) => b.rating - a.rating)
      .slice(0, 5);

    // 勝率ランキング（20戦以上経験者）
    const winRateRanking = world.npcs
      .map((npc) => {
        const wins = npc.wins;
        const losses = npc.losses;
        const total = wins + losses;
        return {
          npc,
          winRate: total >= 20 ? wins / total : 0,
          totalBattles: total,
        };
      })
      .filter((entry) => entry.totalBattles >= 20)
      .sort((a, b) => b.winRate - a.winRate)
      .slice(0, 5);

    return {
      alive,
      dead,
      totalDuels,
      ratingRanking,
      winRateRanking,
    };
  }, [world.npcs]);

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>📊 統計グラフ</h3>

      <div style={styles.statsGrid}>
        {/* 人口統計 */}
        <div style={styles.statCard}>
          <h4 style={styles.cardTitle}>人口統計</h4>
          <div style={styles.popStats}>
            <div style={styles.popItem}>
              <span style={styles.popLabel}>生存:</span>
              <span style={{ ...styles.popValue, color: '#4ade80' }}>
                {stats.alive.length} 人
              </span>
            </div>
            <div style={styles.popItem}>
              <span style={styles.popLabel}>死亡:</span>
              <span style={{ ...styles.popValue, color: '#ef4444' }}>
                {stats.dead.length} 人
              </span>
            </div>
            <div style={styles.popItem}>
              <span style={styles.popLabel}>総数:</span>
              <span style={{ ...styles.popValue, color: '#60a5fa' }}>
                {world.npcs.length} 人
              </span>
            </div>
          </div>
          <PopulationBar alive={stats.alive.length} dead={stats.dead.length} />
        </div>

        {/* デュエル統計 */}
        <div style={styles.statCard}>
          <h4 style={styles.cardTitle}>⚔️ デュエル統計</h4>
          <div style={styles.duelStats}>
            <div style={styles.bigNumber}>{stats.totalDuels.toFixed(0)}</div>
            <div style={styles.bigLabel}>総デュエル数</div>
          </div>
        </div>

        {/* レーティングランキング */}
        <div style={styles.statCard}>
          <h4 style={styles.cardTitle}>⭐ Ratingランキング</h4>
          <div style={styles.rankingList}>
            {stats.ratingRanking.map((npc, index) => (
              <RankingItem
                key={npc.id}
                rank={index + 1}
                npc={npc}
                value={npc.rating}
                maxValue={stats.ratingRanking[0]?.rating || 1}
                onClick={onNPCClick}
              />
            ))}
          </div>
        </div>

        {/* 勝率ランキング */}
        <div style={styles.statCard}>
          <h4 style={styles.cardTitle}>🏆 勝率ランキング (20戦以上)</h4>
          <div style={styles.rankingList}>
            {stats.winRateRanking.map((entry, index) => (
              <RankingItem
                key={entry.npc.id}
                rank={index + 1}
                npc={entry.npc}
                value={entry.winRate * 100}
                maxValue={100}
                suffix="%"
                subtitle={`${entry.totalBattles}戦`}
                onClick={onNPCClick}
              />
            ))}
            {stats.winRateRanking.length === 0 && (
              <div style={styles.emptyMessage}>20戦以上経験者がいません</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 人口バーグラフ
 */
const PopulationBar: React.FC<{ alive: number; dead: number }> = ({ alive, dead }) => {
  const total = alive + dead;
  const alivePercent = total > 0 ? (alive / total) * 100 : 50;
  const deadPercent = total > 0 ? (dead / total) * 100 : 50;

  return (
    <div style={barStyles.container}>
      <div
        style={{
          ...barStyles.bar,
          width: `${alivePercent}%`,
          backgroundColor: '#4ade80',
        }}
      />
      <div
        style={{
          ...barStyles.bar,
          width: `${deadPercent}%`,
          backgroundColor: '#ef4444',
        }}
      />
    </div>
  );
};

const barStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    width: '100%',
    height: '30px',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '12px',
  },
  bar: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
};

/**
 * ランキングアイテム
 */
interface RankingItemProps {
  rank: number;
  npc: SimNPC;
  value: number;
  maxValue: number;
  suffix?: string;
  subtitle?: string;
  onClick?: (npc: SimNPC) => void;
}

const RankingItem: React.FC<RankingItemProps> = ({
  rank,
  npc,
  value,
  maxValue,
  suffix = '',
  subtitle,
  onClick,
}) => {
  const percent = maxValue > 0 ? (value / maxValue) * 100 : 0;

  const getRankColor = (rank: number): string => {
    if (rank === 1) return '#fbbf24'; // Gold
    if (rank === 2) return '#94a3b8'; // Silver
    if (rank === 3) return '#d97706'; // Bronze
    return '#64748b';
  };

  return (
    <div style={rankingStyles.container}>
      <div style={rankingStyles.header}>
        <div style={rankingStyles.rankBadge}>
          <span style={{ ...rankingStyles.rankNumber, color: getRankColor(rank) }}>
            {rank}
          </span>
        </div>
        <div
          style={{
            ...rankingStyles.info,
            cursor: onClick ? 'pointer' : 'default',
          }}
          onClick={() => onClick?.(npc)}
        >
          <span
            style={{
              ...rankingStyles.name,
              opacity: npc.isAlive ? 1 : 0.5,
              textDecoration: onClick ? 'underline' : 'none',
              textDecorationColor: '#666',
            }}
          >
            {npc.name}
            {!npc.isAlive && ' 💀'}
          </span>
          {subtitle && <span style={rankingStyles.subtitle}>{subtitle}</span>}
        </div>
        <span style={rankingStyles.value}>
          {value.toFixed(suffix === '%' ? 1 : 0)}
          {suffix}
        </span>
      </div>
      <div style={rankingStyles.barBackground}>
        <div
          style={{
            ...rankingStyles.barFill,
            width: `${percent}%`,
          }}
        />
      </div>
    </div>
  );
};

const rankingStyles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  rankBadge: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#2a2a3e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  rankNumber: {
    fontSize: '13px',
    fontWeight: 'bold',
  },
  info: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  name: {
    fontSize: '14px',
    color: '#ddd',
  },
  subtitle: {
    fontSize: '11px',
    color: '#888',
  },
  value: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#60a5fa',
    minWidth: '50px',
    textAlign: 'right',
  },
  barBackground: {
    width: '100%',
    height: '6px',
    backgroundColor: '#2a2a3e',
    borderRadius: '3px',
    overflow: 'hidden',
    marginLeft: '32px',
  },
  barFill: {
    height: '100%',
    backgroundColor: '#60a5fa',
    transition: 'width 0.3s ease',
    borderRadius: '3px',
  },
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#1a1a2e',
    border: '2px solid #444',
    borderRadius: '8px',
    padding: '16px',
    overflow: 'hidden',
  },
  title: {
    margin: '0 0 16px 0',
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    overflow: 'hidden',
  },
  statCard: {
    backgroundColor: '#16162a',
    border: '1px solid #2a2a3e',
    borderRadius: '6px',
    padding: '16px',
  },
  cardTitle: {
    margin: '0 0 12px 0',
    color: '#aaa',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  popStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  popItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  popLabel: {
    color: '#888',
    fontSize: '14px',
  },
  popValue: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  duelStats: {
    textAlign: 'center',
    padding: '20px 0',
  },
  bigNumber: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#60a5fa',
    marginBottom: '4px',
  },
  bigLabel: {
    fontSize: '13px',
    color: '#888',
  },
  rankingList: {
    display: 'flex',
    flexDirection: 'column',
  },
  emptyMessage: {
    color: '#666',
    fontSize: '13px',
    textAlign: 'center',
    padding: '20px 0',
  },
};
